import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isString, upperFirst } from 'lodash/fp';
import { DateTime, Settings } from 'luxon';
import * as styles from './Time.css';

Settings.defaultLocale = DateTime.now().resolvedLocaleOptions().locale;

export const TimeFormat = {
    SHORT_RELATIVE: Symbol('SHORT_RELATIVE'),
    RELATIVE: Symbol('RELATIVE'),
    TEMPORAL: Symbol('TEMPORAL'),
    EVENT_HEADER: Symbol('EVENT_HEADER'),
    DATETIME_MED: Symbol('DATETIME_MED'),
    DATE_MED: Symbol('DATE_MED'),
    DATETIME_SHORT: Symbol('DATETIME_SHORT'),
    TIME_SIMPLE: Symbol('TIME_SIMPLE'),
    DATE_SHORT: Symbol('DATETIME_SHORT')
};

const ONE_SECOND = 1;
const ONE_HOUR = 1;

const datify = (date) => (isString(date) ? DateTime.fromISO(date) : date);
// Luxon diffs are returned as negative durations
const diffNow = (unit, date) => Math.abs(datify(date).diffNow(unit)[unit]);
const formatDuration = (duration) => {
    if (duration.as('minutes') < 1) {
        return duration.toFormat("s's'");
    }

    if (duration.as('hours') < 1) {
        return duration.toFormat("m'm'");
    }

    if (duration.as('days') < 1) {
        return duration.toFormat("h'h'");
    }

    return duration.toFormat("d'd'");
};

function formatTimestamp(t, format, d) {
    switch (format) {
        case TimeFormat.SHORT_RELATIVE: {
            const dur = datify(d).diffNow().negate();

            return t('components:Time.ago', { amount: formatDuration(dur) });
        }
        case TimeFormat.RELATIVE: {
            const timeAgoInDays = diffNow('days', d);

            if (diffNow('seconds', d) < ONE_SECOND) {
                return t('components:Time.now');
            }

            if (diffNow('hours', d) < ONE_HOUR) {
                return d.toRelative({ style: 'short' });
            }

            if (timeAgoInDays <= 2) {
                return `${upperFirst(d.toRelativeCalendar())}, ${d.toLocaleString(
                    DateTime.TIME_SIMPLE
                )}`;
            }

            if (timeAgoInDays < 365) {
                return d.toLocaleString({ weekday: 'long', month: 'short', day: 'numeric' });
            }

            return d.toLocaleString({
                weekday: 'long',
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        }

        case TimeFormat.TEMPORAL:
            return diffNow('seconds', d) < ONE_SECOND ? 'now' : d.toRelativeCalendar();

        case TimeFormat.EVENT_HEADER: {
            const timeAgoInDays = diffNow('days', d);

            if (timeAgoInDays < 2) {
                return d.toRelativeCalendar();
            }

            if (timeAgoInDays < 365) {
                return d.toLocaleString({ weekday: 'long', month: 'long', day: '2-digit' });
            }

            return d.toLocaleString(DateTime.DATE_HUGE);
        }

        case TimeFormat.DATETIME_MED:
            return d.toLocaleString(DateTime.DATETIME_MED);

        case TimeFormat.DATE_MED:
            return d.toLocaleString(DateTime.DATE_MED);

        case TimeFormat.DATETIME_SHORT:
            return d.toLocaleString(DateTime.DATETIME_SHORT);

        case TimeFormat.TIME_SIMPLE:
            return d.toLocaleString(DateTime.TIME_SIMPLE);

        case TimeFormat.DATE_SHORT:
            return d.toLocaleString(DateTime.DATE_SHORT);

        default:
            return d.toLocaleString();
    }
}

export function Time({
    testId,
    value = null,
    format = TimeFormat.RELATIVE,
    fallbackValue = null,
    includeTimeZone = false,
    ...props
}) {
    const d = DateTime.fromISO(value);
    const { t } = useTranslation();

    if (!d.isValid) {
        return (
            <span data-testid={testId} title={fallbackValue ?? t('common:notAvailable')} {...props}>
                {fallbackValue ?? t('common:naDashes')}
            </span>
        );
    }

    const title = () => {
        const timestamp = formatTimestamp(t, format, d);

        if (!timestamp) {
            return undefined;
        }

        if (includeTimeZone) {
            return `${timestamp} (${d.zoneName})`;
        }

        return timestamp;
    };

    return (
        <time
            className={styles.base}
            data-testid={testId}
            dateTime={value}
            title={title()}
            {...props}>
            {formatTimestamp(t, format, d)}
            {includeTimeZone && <span className={styles.tz}>({d.zoneName})</span>}
        </time>
    );
}

Time.propTypes = {
    /**
     * Emit a `data-testid` prop on the root element of the component. Use this
     * to help automated testing tools find this particular component.
     */
    testId: PropTypes.string,

    /**
     * An ISO 8601 compatible timestamp string.
     */
    value: PropTypes.string,

    /**
     * Format to display timestamp in.
     */
    format: PropTypes.oneOf(Object.values(TimeFormat)),

    /**
     * The value to display if no time value is provided
     */
    fallbackValue: PropTypes.string,

    /**
     * Appends the time zone name
     * 5/15/2019, 12:00 PM (Europe/Berlin)
     */
    includeTimeZone: PropTypes.bool
};
